import { logger } from '@adornis/base/logging.js';
import { serverContext } from '@adornis/baseql/server/context.js';
import { runInContext } from '@adornis/baseql/server/server.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { loadRawEntityDataByIDOrFail } from '@adornis/bh-util/util.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import { format, subDays } from 'date-fns';
import type { TemplateResult } from 'lit';
import { getEinzelberatungByID } from '../api/einzelberatung.js';
import { getInstitutionByID } from '../api/institution.js';
import { getMailTemplateForInstitution } from '../api/mail-templates.js';
import { bhBaseQlContext } from '../bh-context.js';
import { ColorDefinitions } from '../entities/ColorDefinitions.js';
import { Einzelberatung } from '../entities/einzelberatung.js';
import { InstitutionMailTemplate, MailTemplateType, MailTemplateVars } from '../entities/mail-templates.js';
import { ParticipationEinzelberatung } from '../entities/ParticipationEinzelberatung.js';
import { getMergedProductSettings, SettingColorDefintionProp } from '../entities/settings.js';
import { User } from '../entities/user.js';
import { ParticipationState } from '../enums/enums.js';
import { assembleInstitutionMail } from '../mails/assemble-mail.js';
import { institutionMailHint, institutionMailVariables } from '../mails/mail-template-catalog.js';
import { sendInstitutionEmail } from '../mails/sendInstitutionMail.js';
import { mongo } from '../server/mongo.js';
import { getNotificationType, templateUsesMeetingCodeOrPassword } from './_helper.js';

defineMailTemplate(MailTemplateType.EINZELBERATUNG_MEETING_INFOS, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Termininformationen zur Einzelberatung',
    content: `
          <p>Hallo {{title}} {{firstname}} {{lastname}},</p>
<p>Ihre Einzelberatung findet in wenigen Tagen statt. Der Termin findet digital in der Form eines Videocalls unter folgendem Link statt:</p>
<p><a>{{${MailTemplateVars.MEETING_LINK}}}</a></p>
<p>{{${MailTemplateVars.PRODUCT_CARD}}}</p>
<p>Viele Gr&uuml;&szlig;e</p>
<p>---------------------------------------------------------------------</p>
<p>Hello {{title}} {{firstname}} {{lastname}},</p>
<p>Your individual counseling session will take place in a few days. It will be held digitally as a video call at the following link:</p>
<p><a>{{${MailTemplateVars.MEETING_LINK}}}</a></p>
<p>{{${MailTemplateVars.PRODUCT_CARD_EN}}}</p>
<p>Best regards</p>
        `,
    footer: '',
  },
  variables: institutionMailVariables(MailTemplateType.EINZELBERATUNG_MEETING_INFOS),
  attachments: [],
  displayName: 'Einzelberatung — Termin Informationen',
  editorHint: () => institutionMailHint(MailTemplateType.EINZELBERATUNG_MEETING_INFOS),
});

function renderMeetingLinkHtml(
  link?: string | null,
  code?: string | null,
  password?: string | null,
  options?: { omitCodeAndPassword?: boolean },
) {
  if (!link) return '';
  let out = `<a href="${link}">${link}</a>`;
  if (!options?.omitCodeAndPassword) {
    if (code) out += ` Code: ${code}`;
    if (password) out += ` Passwort: ${password}`;
  }
  return out;
}

export async function notifyEinzelberatungMeetingInfos(data: { participationID: string }) {
  const participationCol = mongo().collection<ParticipationEinzelberatung>(ParticipationEinzelberatung._collectionName);
  const participation = await participationCol.raw.findOne<ParticipationEinzelberatung>({ _id: data.participationID });
  if (!participation) throw new Error('participation not found: ' + data.participationID);
  if (![ParticipationState.in, ParticipationState.participated].includes(participation.state)) {
    logger.info({ participationID: data.participationID, state: participation.state }, 'Participation is neither enrolled nor participated. No counseling meeting info notification was sent.');
    return;
  }

  const userCol = mongo().collection<User>(User._collectionName);
  const user = await userCol.raw.findOne<User>({ _id: participation.userID });
  if (!user) throw new Error('user not found: ' + participation.userID);

  const einzelberatung = await runInContext(serverContext.sessionID, () => getEinzelberatungByID(participation.productID)({
    _id: 1,
    _class: 1,
    isCanceled: 1,
    institutionID: 1,
    name: 1,
    slotConfig: {
      _id: 1,
      start: 1,
      end: 1,
      slotDuration: 1,
      beraterMeetingLinks: { beraterID: 1, meetingLink: { link: 1, code: 1, password: 1 } },
    },
    slots: { _id: 1, start: 1, end: 1, beraterID: 1 },
    settings: {
      _id: 1,
      _class: 1,
      evaluationLink: 1,
      inheritedEvaluationLink: 1,
      evaluationLinkCode: 1,
      inheritedEvaluationLinkCode: 1,
    },
  }));

  if (!einzelberatung) {
    logger.info({ _id: participation.productID }, 'No einzelberatung found — skipping meeting info mail.');
    throw new Error('No einzelberatung found for given id');
  }

  if (einzelberatung.isCanceled) {
    logger.info({ productID: einzelberatung._id, participationID: data.participationID }, 'Counseling is canceled. No meeting info notification was sent.');
    return;
  }

  const slot = einzelberatung.slots?.find(s => s._id === participation.timeslotID);
  if (!slot) throw new Error('slot not found: ' + participation.timeslotID);

  const beraterLink = einzelberatung.slotConfig?.beraterMeetingLinks?.find(bml => bml.beraterID === slot.beraterID);

  const institution = await getInstitutionByID(einzelberatung.institutionID)({
    _id: 1,
    name: 1,
    colorDefinitions: selectionSet(() => ColorDefinitions, 4),
    settings: {
      colorDefinitions: selectionSet(() => ColorDefinitions, 4),
      inheritedColorDefinitions: selectionSet(() => SettingColorDefintionProp, 4),
    },
  });
  if (!institution) throw new Error('institution not found');

  bhBaseQlContext.colorDefinitions =
    institution?.settings?.colorDefinitions ?? institution?.settings?.inheritedColorDefinitions?.value;

  const mailTemplate = await getMailTemplateForInstitution(
    einzelberatung.institutionID,
    MailTemplateType.EINZELBERATUNG_MEETING_INFOS,
  )(InstitutionMailTemplate.allFields);

  const mailVars = {
    [MailTemplateVars.TITLE]: user?.title ?? '',
    [MailTemplateVars.FIRST_NAME]: InstitutionMailTemplate.mapFirstNameVar(user),
    [MailTemplateVars.LAST_NAME]: InstitutionMailTemplate.mapLastNameVar(user),
    [MailTemplateVars.PRODUCT_NAME]: einzelberatung.name,
    [MailTemplateVars.PRODUCT_CARD]: InstitutionMailTemplate.mapProductVarEinzelberatung(
      einzelberatung as Einzelberatung,
      slot.start!,
    ),
    [MailTemplateVars.PRODUCT_CARD_EN]: InstitutionMailTemplate.mapProductVarEinzelberatung(
      einzelberatung as Einzelberatung,
      slot.start!,
      'en',
    ),
    [MailTemplateVars.MEETING_LINK]: renderMeetingLinkHtml(
      beraterLink?.meetingLink?.link,
      beraterLink?.meetingLink?.code,
      beraterLink?.meetingLink?.password,
      { omitCodeAndPassword: templateUsesMeetingCodeOrPassword({ subject: mailTemplate.subject, content: mailTemplate.content }) },
    ),
    [MailTemplateVars.MEETING_CODE]: beraterLink?.meetingLink?.code ?? '',
    [MailTemplateVars.MEETING_PASSWORD]: beraterLink?.meetingLink?.password ?? '',
    [MailTemplateVars.EVENT_DATES]: slot.start ? format(slot.start, 'dd.MM.yyyy') : 'tbd',
    [MailTemplateVars.EVALUATION_LINK]:
      einzelberatung.settings.evaluationLink ?? einzelberatung.settings.inheritedEvaluationLink?.[1] ?? '',
    [MailTemplateVars.EVALUATION_CODE]:
      einzelberatung.settings.evaluationLinkCode ?? einzelberatung.settings.inheritedEvaluationLinkCode?.[1] ?? '',
  };

  const notificationType = await getNotificationType(user, mailTemplate, einzelberatung.institutionID);
  if (notificationType !== 'none' && mailTemplate.emailNotification && user.email) {
    const { subject, content } = await assembleInstitutionMail({ template: mailTemplate, infos: mailVars as any });
    if (!content) throw new Error('mail template has no content!');
    if (!subject) throw new Error('mail template has no subject!');
    await sendInstitutionEmail({
      institutionID: einzelberatung.institutionID,
      email: user.email,
      subject,
      content: content as TemplateResult<1>,
    });
  } else {
    logger.warn(
      { notificationType, notification: mailTemplate.emailType },
      'no einzelberatung meeting info mail sent — email channel not active',
    );
  }
}

/**
 * Sends the mail for a freshly-enrolled participant if the scheduled dispatch date has already passed.
 * Mirrors sendMeetingInfoMailsIfAlreadySent for workshops.
 */
export async function sendEinzelberatungMeetingInfoMailsIfAlreadySent(
  participationID: string,
  productID: string,
  timeslotID: string | null | undefined,
) {
  // Callers fire-and-forget — guard against silent unhandled rejections.
  try {
    if (!timeslotID) return;

    const einzelberatung = await runInContext(serverContext.sessionID, () => loadRawEntityDataByIDOrFail(Einzelberatung, productID, {
      _id: 1,
      slots: { _id: 1, start: 1 },
    }));

    const slot = einzelberatung.slots?.find(s => s._id === timeslotID);
    if (!slot?.start) return;

    const productSettings = await getMergedProductSettings(productID, ['einzelberatungMeetingInfoMailLeadTime']);
    const leadDays = productSettings.einzelberatungMeetingInfoMailLeadTime;
    if (typeof leadDays !== 'number' || leadDays < 0) return;

    const now = new Date();
    if (now > subDays(slot.start, leadDays)) {
      notifyEinzelberatungMeetingInfos({ participationID }).catch(err =>
        logger.error({ err, participationID, productID }, 'late einzelberatung meeting-info dispatch failed'),
      );
    }
  } catch (err) {
    logger.error({ err, participationID, productID }, 'sendEinzelberatungMeetingInfoMailsIfAlreadySent setup failed');
  }
}
